// @ts-strict-ignore
import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  getPropertyForContent,
  refreshHighchartsSize,
  VisualizationContentProps,
  VisualizationContentPropsTransformer,
} from '@/annotation/ckEditorPlugins/components/content.utilities';
import { sqContentApi } from '@/sdk/api/ContentApi';
import _ from 'lodash';
import { XyPlotContent } from '@/scatterPlot/XyPlotContent.organism';
import { ErrorBoundaryWithLogging } from '@/core/ErrorBoundary.atom';
import { forceRefreshContent } from '@/annotation/reportContent.utilities';
import { ErrorFallbackMinimal } from '@/core/ErrorFallback.organism';
import { Visualization } from '@/annotation/ckEditorPlugins/components/content.utilities.constants';
import { ChartViewContent } from '@/reportEditor/ChartViewContent.molecule';
import { CONTENT_LOADING_CLASS, IMAGE_BORDER_CLASS } from '@/reportEditor/report.constants';
import { sqReportStore } from '@/core/core.stores';
import { applyContentUpgrade } from '@/annotation/ckEditorPlugins/components/interactiveContentUpgrader.utilities';
import { EmptyContent } from '@/reportEditor/EmptyContent.molecule';
import { ContentDisplayMode } from '@/annotation/ckEditorPlugins/CKEditorPlugins.constants';
import { DEBOUNCE } from '@/core/core.constants';
import { TableBuilderContent } from '@/tableBuilder/TableBuilderContent.molecule';
import { PluginContent } from '@/plugin/PluginContent.molecule';
import type { ContentMeasurements } from '@/tableBuilder/tableBuilder.types';

const renderComponent: (visualization: Visualization, props: any) => JSX.Element = (
  visualization: Visualization,
  props: any,
) => {
  const InnerComponent =
    {
      [Visualization.TABLE]: TableBuilderContent,
      [Visualization.XY_PLOT]: XyPlotContent,
      [Visualization.TREND]: ChartViewContent,
      [Visualization.PLUGIN]: PluginContent,
      [Visualization.NONE]: EmptyContent,
    }[visualization] || null;
  if (_.isNull(InnerComponent)) {
    throw new Error('No visualization type found in React JSON blob');
  }
  return (
    <div
      data-testid={`innerContainer-${props.contentId}`}
      className={classNames('inheritMinHeightAndHeight flexRowContainer reactJsonContent inheritHeight', {
        tableVisualization: visualization === Visualization.TABLE && !props.showChartView,
      })}>
      <InnerComponent {...props} />
    </div>
  );
};

interface ReactJsonContentProps {
  contentId: string;
  style: any;
  displayMode: ContentDisplayMode;
  loading: boolean;
  target: any;
  src: string;
  border?: boolean;
  onLoad: (properties: { visualization: Visualization }) => void;
  onError: (error: string, errorCode: number) => void;
  onMeasurementsReady: (width: number | undefined, height: number | undefined) => void;
  showMenu?: () => void;
  error?: string;
  extraClassNames: string;
  errorClass?: string;
  propertyOverrides?: any;
  isInView: boolean;
  height?: number;
  width?: number;
  darkMode?: boolean;
  updateContentMeasurements: (measurements: ContentMeasurements) => void;
  editorId: string;
}

/**
 * Handles React JSON visualizations. The properties state object is expected to the props to a props only version of a
 * Analysis visualization.
 */
export const ReactJsonContent: React.FunctionComponent<ReactJsonContentProps> = ({
  contentId,
  extraClassNames,
  errorClass,
  style,
  displayMode,
  loading,
  target,
  src,
  onLoad,
  onError,
  border,
  onMeasurementsReady,
  showMenu = () => null,
  error,
  propertyOverrides,
  isInView,
  height = 0,
  width = 0,
  darkMode,
  updateContentMeasurements,
  editorId,
}) => {
  const [properties, setProperties] = useState<any | undefined>();
  const isCurrentlyLoading = useRef(false);

  const shouldNotFetchBlob = isInView === undefined;

  useEffect(() => {
    if (shouldNotFetchBlob) {
      return;
    }

    if (isCurrentlyLoading.current) {
      /**
       * at this point there's an ongoing getReactBlob call, so we shouldn't do a new one
       * this is usually the case when the browser doesn't have enough time between auto-updates
       * to re-render all the interactive content elements
       * we should also show some kind of warning that there's a lot of contents so we suggest increasing
       * the auto-update interval
       */
      return;
    }
    const queryParams = new URLSearchParams(src.substr(src.indexOf('?')));
    const params = {
      hash: queryParams.get('hash'),
    };
    const asyncDisabled = queryParams.get('useAsync') !== 'true';
    isCurrentlyLoading.current = true;
    sqContentApi
      .getReactBlob({ id: contentId }, { params, useManualAsync: true, asyncDisabled })
      .then(({ data }) => {
        const untypedProperties = data as any;
        if (untypedProperties.visualization) {
          const propertiesForVisualizationType = applyContentUpgrade(
            VisualizationContentPropsTransformer[untypedProperties.visualization]({
              ...untypedProperties,
              isContent: true,
              darkMode,
              contentId,
              editorId,
              ...VisualizationContentProps[untypedProperties.visualization],
            }),
            untypedProperties.version ?? 0,
          );

          setProperties(propertiesForVisualizationType);
          if (
            displayMode === ContentDisplayMode.PDF &&
            propertiesForVisualizationType.visualization === Visualization.TREND
          ) {
            propertiesForVisualizationType.onHighchartsLoad = () => onLoad(propertiesForVisualizationType);
          } else if (
            propertiesForVisualizationType.visualization === Visualization.TABLE &&
            !(propertyOverrides?.showChartView || propertiesForVisualizationType.showChartView) &&
            isInView
          ) {
            propertiesForVisualizationType.onAgGridReady = _.debounce(
              () => onLoad(propertiesForVisualizationType),
              DEBOUNCE.LONG,
            );
          } else if (propertiesForVisualizationType.visualization === Visualization.PLUGIN) {
            propertiesForVisualizationType.onPluginReady = () => onLoad(propertiesForVisualizationType);
            propertiesForVisualizationType.onClick = showMenu;
          } else {
            onLoad(propertiesForVisualizationType);
          }
        }
      })
      .catch((error) => {
        onError(error.data?.statusMessage, error.status);
      })
      .finally(() => {
        isCurrentlyLoading.current = false;
      });
  }, [src, darkMode, shouldNotFetchBlob]);

  const measurementsCallback = useCallback(() => {
    const content = sqReportStore.getContentById(contentId);
    if (
      properties.visualization === Visualization.TABLE &&
      !getPropertyForContent('showChartView', propertyOverrides, properties)
    ) {
      // If it is a table, the width is calculated by ag-grid and should not be reset every time the content component is mounted
      // The content component is mounted when scrolled into view and vice versa (CRAB-42238)
      onMeasurementsReady(undefined, 0);
    } else {
      onMeasurementsReady(content?.width ?? target.current.clientWidth, content?.height ?? target.current.clientHeight);
    }
  }, [properties, contentId]);

  useEffect(() => {
    if (!loading && properties) {
      refreshHighchartsSize(
        contentId,
        height ? height : target.current.clientHeight,
        properties.visualization,
        properties.subVisualization,
      );
    }
  }, [contentId, target, width, height, properties, loading]);

  const getStyle = (errorClass: string) => {
    const width = errorClass && errorClass !== CONTENT_LOADING_CLASS.NO_CAPSULE_ERROR ? 'fit-content' : 'inherit';

    const styleOverrides: any = {
      maxHeight: undefined,
      maxWidth: undefined,
      width,
      height: 'inherit',
    };

    return {
      ...style,
      ...styleOverrides,
    };
  };

  const retry = () => forceRefreshContent(contentId);

  return (
    <div
      data-testid={`content-${contentId}`}
      data-seeq-content={contentId}
      data-visualization={properties?.visualization ?? 'none'}
      data-sub-visualization={properties?.subVisualization ?? 'none'}
      style={getStyle(errorClass)}
      ref={target}
      title={error}
      className={classNames('inheritMinHeight', extraClassNames, errorClass ?? '', {
        [IMAGE_BORDER_CLASS]: border,
        [CONTENT_LOADING_CLASS.SPINNER]: loading && !errorClass,
        [CONTENT_LOADING_CLASS.LOADED]: !loading && !errorClass,
      })}>
      {!errorClass && properties && (
        <ErrorBoundaryWithLogging fallback={(error) => <ErrorFallbackMinimal error={error} retry={retry} />}>
          {!isInView && (
            <div
              data-testid={`notRenderedContainer-${contentId}`}
              className={CONTENT_LOADING_CLASS.NOT_RENDERED}
              // Retain the height of the parent when it scrolls out of view to avoid jumpiness
              style={{ width: target.current.clientWidth, height: target.current.clientHeight }}
            />
          )}
          {isInView &&
            renderComponent(properties.visualization, {
              ...properties,
              ...propertyOverrides,
              onContentLoad: measurementsCallback,
              afterChartUpdate: measurementsCallback,
              updateContentMeasurements,
            })}
        </ErrorBoundaryWithLogging>
      )}
    </div>
  );
};
